// IndexedDB-backed blob cache for large, rarely-changing images (e.g. the
// floor-plan map). Keyed by the remote image URL so an unchanged image is served
// straight from the user's own machine — no network round-trip on reload.
//
// Bounded by MAX_ENTRIES with least-recently-used eviction so caching many
// images (multiple locations/floors, re-uploads) can never grow without limit.

const DB_NAME = 'sd-image-cache';
const STORE_NAME = 'images';
const DB_VERSION = 1;
const MAX_ENTRIES = 30;

export interface CachedImage {
  blob: Blob;
  ts: number; // when the blob was stored (used to decide staleness)
}

interface CacheRecord extends CachedImage {
  lastAccess: number; // updated on read, drives LRU eviction
}

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));

      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
};

export const getCachedImage = async (url: string): Promise<CachedImage | null> => {
  try {
    const db = await openDB();

    return await new Promise<CachedImage | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(url);

      req.onsuccess = () => {
        const record = req.result as CacheRecord | undefined;

        if (!record) {
          resolve(null);

          return;
        }

        // Bump last-access so this entry survives LRU eviction.
        store.put({ ...record, lastAccess: Date.now() }, url);
        resolve({ blob: record.blob, ts: record.ts });
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
};

// Drop the least-recently-used entries once the store exceeds MAX_ENTRIES.
const evictIfNeeded = async (db: IDBDatabase): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const countReq = store.count();

    countReq.onsuccess = () => {
      const overflow = countReq.result - MAX_ENTRIES;

      if (overflow <= 0) {
        resolve();

        return;
      }

      const entries: { key: IDBValidKey; lastAccess: number }[] = [];
      const cursorReq = store.openCursor();

      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;

        if (cursor) {
          const record = cursor.value as CacheRecord;

          entries.push({ key: cursor.primaryKey, lastAccess: record.lastAccess ?? 0 });
          cursor.continue();

          return;
        }

        entries
          .sort((a, b) => a.lastAccess - b.lastAccess)
          .slice(0, overflow)
          .forEach(entry => store.delete(entry.key));
      };
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export const putCachedImage = async (url: string, blob: Blob): Promise<void> => {
  try {
    const db = await openDB();
    const now = Date.now();

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const record: CacheRecord = { blob, ts: now, lastAccess: now };

      tx.objectStore(STORE_NAME).put(record, url);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    await evictIfNeeded(db);
  } catch {
    // Caching is best-effort; ignore write failures (e.g. private mode / quota).
  }
};
